import { useState, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setStatus("submitting");

    const formData = new FormData(formRef.current);
    const payload = Object.fromEntries(formData.entries());

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStatus("success");
        formRef.current.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/contact/contact-hero.png"
              alt="Contact Perspective Therapy"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)" }} />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Get in Touch
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Reach out{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  we're here to help
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  Have questions about therapy, pricing, or finding the right fit? We're happy to hear from you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact content */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-12 xl:gap-20">
              {/* Left: Contact info + form */}
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #6B742F, #3C6FF8)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#6B742F" }}>
                      Contact Us
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Let's start a conversation
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-10 max-w-lg">
                    Whether you're ready to book, have questions about the process, or just want to learn more about how therapy works at Perspective, we're here.
                  </p>
                </ScrollReveal>

                {/* Contact info cards */}
                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                    {[
                      {
                        icon: "ri-map-pin-line",
                        label: "Office Address",
                        lines: ["4 Gilder Street, Suite 2", "Larchmont, NY 10538"],
                        color: "#3C6FF8",
                        bg: "rgba(60,111,248,0.09)",
                      },
                      {
                        icon: "ri-phone-line",
                        label: "Phone",
                        lines: ["(838) 910-8150"],
                        href: "tel:+18389108150",
                        color: "#7D54FC",
                        bg: "rgba(125,84,252,0.09)",
                      },
                      {
                        icon: "ri-mail-line",
                        label: "Email",
                        lines: ["Info@perspectivetherapyNY.com"],
                        href: "mailto:Info@perspectivetherapyny.com",
                        color: "#6B742F",
                        bg: "rgba(107,116,47,0.09)",
                      },
                      {
                        icon: "ri-time-line",
                        label: "Business Hours",
                        lines: ["Mon–Fri: 9am – 9pm", "Sat & Sun: 10am – 4pm"],
                        color: "#3C6FF8",
                        bg: "rgba(60,111,248,0.09)",
                      },
                    ].map((item) => (
                      <div key={item.label} className="rounded-xl p-5 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: item.bg }}>
                            <i className={`${item.icon} text-sm`} style={{ color: item.color }}></i>
                          </div>
                          <span className="font-manrope text-[13px] font-semibold text-[#2B2B2B]/80">{item.label}</span>
                        </div>
                        {item.lines.map((line) => (
                          <p key={line} className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-snug">
                            {item.href ? (
                              <a href={item.href} className="hover:text-[#2B2B2B] transition-colors cursor-pointer">
                                {line}
                              </a>
                            ) : (
                              line
                            )}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                {/* Form */}
                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="rounded-2xl p-6 md:p-8 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-5">Send us a message</h3>
                    {status === "success" ? (
                      <div
                        className="rounded-xl border px-5 py-7 sm:px-6 sm:py-8"
                        style={{ borderColor: "rgba(107,116,47,0.35)", background: "rgba(107,116,47,0.08)" }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center" style={{ background: "rgba(107,116,47,0.18)" }}>
                            <i className="ri-checkbox-circle-line text-2xl" style={{ color: "#6B742F" }}></i>
                          </div>
                          <div>
                            <h4 className="font-fraunces text-[#2B2B2B] text-2xl leading-tight font-medium mb-2">
                              Thank you! Your message has been sent.
                            </h4>
                            <p className="font-manrope text-[15px] text-[#2B2B2B]/80">We'll be in touch soon.</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <form
                      ref={formRef}
                      onSubmit={handleSubmit}
                      
                      className="flex flex-col gap-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="firstName" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                            First Name
                          </label>
                          <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            required
                            className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all"
                            style={{ borderColor: "rgba(203,191,175,0.45)" }}
                            placeholder="Your first name"
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                            Last Name
                          </label>
                          <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            required
                            className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all"
                            style={{ borderColor: "rgba(203,191,175,0.45)" }}
                            placeholder="Your last name"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="email" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                          Email
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all"
                          style={{ borderColor: "rgba(203,191,175,0.45)" }}
                          placeholder="your@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                          Phone (optional)
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all"
                          style={{ borderColor: "rgba(203,191,175,0.45)" }}
                          placeholder="(555) 000-0000"
                        />
                        <p className="font-manrope text-[11px] text-[#2B2B2B]/50 mt-1">
                          Learn how we handle contact information in our <a href="/privacy-policy" className="text-[#3C6FF8] hover:underline">Privacy Policy</a>.
                        </p>
                      </div>

                      <div>
                        <label htmlFor="interest" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                          I'm interested in
                        </label>
                        <select
                          id="interest"
                          name="interest"
                          required
                          className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all cursor-pointer"
                          style={{ borderColor: "rgba(203,191,175,0.45)" }}
                        >
                          <option value="">Select an option</option>
                          <option value="individual">Individual Therapy</option>
                          <option value="couples">Couples Therapy</option>
                          <option value="teen">Teen Therapy</option>
                          <option value="other">Something else</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block font-manrope text-[13px] font-semibold text-[#2B2B2B]/80 mb-1.5">
                          Message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full font-manrope text-sm px-4 py-3 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all resize-none"
                          style={{ borderColor: "rgba(203,191,175,0.45)" }}
                          placeholder="Tell us what's on your mind..."
                        />
                        <p className="font-manrope text-[11px] text-[#2B2B2B]/50 mt-1">Max 500 characters</p>
                      </div>

                      <button
                        type="submit"
                        disabled={status === "submitting"}
                        className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto disabled:opacity-60"
                        style={{ background: "#3C6FF8" }}
                      >
                        {status === "submitting" ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i> Sending...
                          </>
                        ) : (
                          <>
                            Send Message <i className="ri-send-plane-line"></i>
                          </>
                        )}
                      </button>

                      {status === "error" && (
                        <div className="flex items-center gap-2 font-manrope text-[14px] text-red-600">
                          <i className="ri-error-warning-line"></i> Something went wrong. Please try again.
                        </div>
                      )}
                    </form>
                    )}
                  </div>
                </ScrollReveal>
              </div>

              {/* Right: Map + quick CTA */}
              <div className="flex flex-col gap-6">
                <ScrollReveal variant="fadeUp" delay={80}>
                  <div className="rounded-2xl overflow-hidden border h-[300px] md:h-[340px]" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3014.374744377089!2d-73.752258!3d40.927094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c28e8a3e7a3e1f%3A0x3e7a3e1f!2s4%20Gilder%20St%2C%20Larchmont%2C%20NY%2010538!5e0!3m2!1sen!2sus!4v1700000000000"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Perspective Therapy office location"
                      aria-label="Google map showing Perspective Therapy office in Larchmont, NY"
                    />
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={160}>
                  <div className="rounded-2xl p-6 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-3">Not ready to message?</h3>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[14px] leading-relaxed mb-4">
                      Book a free 15-minute consultation call to ask questions and find the right fit. No commitment required.
                    </p>
                    <a
                      href="/therapists"
                      className="inline-flex items-center gap-2 font-manrope font-semibold text-[14px] px-5 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                      style={{ color: "#2B2B2B", borderColor: "rgba(43,43,43,0.18)" }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                        (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                        (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                      }}
                    >
                      Browse Therapists <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="rounded-2xl p-6 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(60,111,248,0.09)" }}>
                        <i className="ri-message-3-line text-sm" style={{ color: "#3C6FF8" }}></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium">Chat with us</h3>
                    </div>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[14px] leading-relaxed mb-4">
                      Have a quick question? Our assistant is available 24/7 to help with scheduling, services, and general inquiries.
                    </p>
                    <div className="flex flex-col gap-2.5">
                      <button
                        className="inline-flex items-center gap-2 font-manrope font-semibold text-[14px] px-5 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                        style={{ color: "#2B2B2B", borderColor: "rgba(43,43,43,0.18)" }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = "#2B2B2B";
                          (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                          (e.currentTarget as HTMLButtonElement).style.color = "#2B2B2B";
                        }}
                        onClick={() => {
                          if (typeof window !== "undefined") {
                            window.dispatchEvent(new CustomEvent("open-perspective-chat"));
                          }
                        }}
                      >
                        Open Chat <i className="ri-chat-3-line"></i>
                      </button>
                    </div>
                  </div>
                </ScrollReveal>

                {/* Connect With Us */}
                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="rounded-2xl p-6 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(125,84,252,0.09)" }}>
                        <i className="ri-share-line text-sm" style={{ color: "#7D54FC" }}></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium">Connect With Us</h3>
                    </div>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[14px] leading-relaxed mb-5">
                      Follow Perspective Therapy for mental health tips, behind-the-scenes practice moments, and community updates.
                    </p>
                    <div className="flex flex-col gap-3">
                      {[
  { icon: "ri-instagram-line", label: "Instagram", handle: "@perspectivetherapyny", href: "https://www.instagram.com/perspectivetherapyny", color: "#E4405F" },
  { icon: "ri-facebook-circle-line", label: "Facebook", handle: "Perspective Therapy", href: "https://www.facebook.com/people/Perspective-Therapy/61572358177918/", color: "#1877F2" },
  { icon: "ri-tiktok-line", label: "TikTok", handle: "@perspectivetherapy", href: "https://www.tiktok.com/@perspectivetherapy", color: "#000000" },
  { icon: "ri-threads-line", label: "Threads", handle: "@perspectivetherapyny", href: "https://www.threads.com/@perspectivetherapyny", color: "#000000" },
  { icon: "ri-twitter-x-line", label: "X / Twitter", handle: "@prspctvtherapy", href: "https://x.com/prspctvtherapy", color: "#000000" },
  { icon: "ri-linkedin-box-line", label: "LinkedIn", handle: "Perspective Therapy", href: "https://www.linkedin.com/company/perspective-therapy-mental-health-counseling-pllc/?viewAsMember=true", color: "#0A66C2" },
].map((s) => (
                        <a
                          key={s.label}
                          href={s.href}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer group"
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = s.color + "10";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                          }}
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ background: s.color + "12" }}>
                            <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
                          </div>
                          <div className="flex flex-col">
                            <span className="font-manrope text-[14px] font-semibold text-[#2B2B2B]">{s.label}</span>
                            <span className="font-manrope text-[12px] text-[#2B2B2B]/55">{s.handle}</span>
                          </div>
                          <i className="ri-arrow-right-up-line text-xs ml-auto opacity-30 group-hover:opacity-70 transition-opacity"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
